import React, { useState } from 'react';

interface MCQPulseLogoProps {
  className?: string;
  size?: number;
}

export const MCQPulseLogo: React.FC<MCQPulseLogoProps> = ({ 
  className = '', 
  size = 40 
}) => {
  const [imgFailed, setImgFailed] = useState(false);

  // If the image loaded successfully, render the exact custom image upload
  if (!imgFailed) {
    return (
      <img 
        src="/NEW_LOGO.webp" 
        alt="MCQ Pulse™ Logo" 
        width={size} 
        height={size} 
        loading="eager"
        fetchPriority="high"
        className={`object-contain select-none max-w-full rounded-full ${className}`}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          if (target.src.endsWith('/NEW_LOGO.webp')) {
            target.src = '/NEW_LOGO.jpg';
          } else {
            setImgFailed(true);
          }
        }}
      />
    );
  }

  // Beautiful vector-drawn SVG fallback (rendered in case the image is not yet uploaded)
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 200 200" 
      className={`select-none ${className}`}
      aria-label="MCQ Pulse™ Logo (Fallback)"
    >
      <defs>
        {/* Load the Google Font matching the bubbly, rounded text of the logo */}
        <style>
          {`
            .logo-text-top {
              font-family: 'Fredoka', 'Arial Rounded MT Bold', 'Nunito', 'Comic Sans MS', sans-serif;
              font-weight: 900;
              font-size: 38px;
            }
            .logo-text-bottom {
              font-family: 'Fredoka', 'Arial Rounded MT Bold', 'Nunito', 'Comic Sans MS', sans-serif;
              font-weight: 900;
              font-size: 32px;
            }
            .logo-question-mark {
              font-family: 'Fredoka', 'Arial Rounded MT Bold', 'Nunito', sans-serif;
              font-weight: 900;
              fill: rgba(255, 255, 255, 0.95);
              text-shadow: 0 1px 2px rgba(0,0,0,0.2);
            }
          `}
        </style>

        {/* Arched path for MCQ text (top curve, left-to-right) */}
        <path 
          id="logo-top-text-path" 
          d="M 28,100 A 72,72 0 0,1 172,100" 
          fill="none" 
        />

        {/* Arched path for PULSE text (bottom curve, left-to-right) */}
        <path 
          id="logo-bottom-text-path" 
          d="M 28,100 A 72,72 0 0,0 172,100" 
          fill="none" 
        />

        {/* Left-to-right gradient matching the dark green-to-red center circle */}
        <linearGradient id="logo-center-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#244B32" />
          <stop offset="35%" stopColor="#2E523A" />
          <stop offset="65%" stopColor="#5E332B" />
          <stop offset="100%" stopColor="#582921" />
        </linearGradient>

        {/* Clipping path to restrict elements within the main inner circular bounds */}
        <clipPath id="logo-inner-bounds">
          <circle cx="100" cy="100" r="95" />
        </clipPath>
      </defs>

      {/* Single clean outer dark border ring, just like the image */}
      <circle 
        cx="100" 
        cy="100" 
        r="98" 
        fill="white" 
        stroke="#1a1a1a" 
        strokeWidth="1.2" 
      />

      {/* Inner boundary circle for layout structure */}
      <circle 
        cx="100" 
        cy="100" 
        r="95" 
        fill="none" 
        stroke="#1a1a1a" 
        strokeWidth="1.2" 
      />

      {/* Content clipped to the inner boundary */}
      <g clipPath="url(#logo-inner-bounds)">
        {/* Left Wing (Green) with perfect organic curvature from the image */}
        <path 
          d="M 6,75 C 32,92 48,82 64.6,64.6 A 50,50 0 0,0 64.6,135.4 C 48,118 32,108 6,125 A 95,95 0 0,1 6,75 Z" 
          fill="#1C6C3C" 
          stroke="#1a1a1a" 
          strokeWidth="1.2" 
          strokeLinejoin="round"
        />

        {/* Right Wing (Red) matching the exact curvature and symmetry */}
        <path 
          d="M 194,75 A 95,95 0 0,1 194,125 C 168,108 152,118 135.4,135.4 A 50,50 0 0,0 135.4,64.6 C 152,82 168,92 194,75 Z" 
          fill="#D83227" 
          stroke="#1a1a1a" 
          strokeWidth="1.2" 
          strokeLinejoin="round"
        />

        {/* Circuit Traces inside Left Wing */}
        <g stroke="rgba(255, 255, 255, 0.2)" strokeWidth="1" fill="none" strokeLinecap="round">
          <path d="M 10,85 L 38,85 L 44,91 L 58,91" />
          <path d="M 12,115 L 32,115 L 38,109 L 52,109" />
          <path d="M 8,100 L 36,100" />
        </g>

        {/* Circuit Traces inside Right Wing */}
        <g stroke="rgba(255, 255, 255, 0.2)" strokeWidth="1" fill="none" strokeLinecap="round">
          <path d="M 190,85 L 162,85 L 156,91 L 142,91" />
          <path d="M 188,115 L 168,115 L 162,109 L 148,109" />
          <path d="M 192,100 L 164,100" />
        </g>

        {/* Center Circle with rich gradient */}
        <circle 
          cx="100" 
          cy="100" 
          r="50" 
          fill="url(#logo-center-grad)" 
          stroke="#1a1a1a" 
          strokeWidth="1.5" 
        />

        {/* Circuit Traces behind the heartbeat pulse line */}
        <g stroke="rgba(255, 255, 255, 0.15)" strokeWidth="1" fill="none" strokeLinecap="round">
          <path d="M 60,90 L 140,90" />
          <path d="M 55,110 L 145,110" />
          <path d="M 50,100 L 60,100" />
          <path d="M 140,100 L 150,100" />
        </g>

        {/* Scattered white question marks exactly positioned as in the image */}
        <g className="logo-question-mark">
          {/* Left Green Wing */}
          <text x="20" y="70" fontSize="11" transform="rotate(-15, 20, 70)" textAnchor="middle">?</text>
          <text x="32" y="98" fontSize="13" transform="rotate(10, 32, 98)" textAnchor="middle">?</text>
          <text x="26" y="128" fontSize="11" transform="rotate(-5, 26, 128)" textAnchor="middle">?</text>

          {/* Center Circle Left (Green Side) */}
          <text x="84" y="80" fontSize="12" transform="rotate(-12, 84, 80)" textAnchor="middle">?</text>
          <text x="68" y="95" fontSize="12" transform="rotate(5, 68, 95)" textAnchor="middle">?</text>
          <text x="72" y="122" fontSize="11" transform="rotate(15, 72, 122)" textAnchor="middle">?</text>

          {/* Center Circle Center / Top */}
          <text x="105" y="65" fontSize="13" textAnchor="middle">?</text>
          <text x="126" y="80" fontSize="12" transform="rotate(8, 126, 80)" textAnchor="middle">?</text>

          {/* Center Circle Right (Red Side) */}
          <text x="135" y="95" fontSize="12" transform="rotate(-10, 135, 95)" textAnchor="middle">?</text>
          <text x="116" y="132" fontSize="13" transform="rotate(-15, 116, 132)" textAnchor="middle">?</text>
          <text x="132" y="128" fontSize="11" transform="rotate(5, 132, 128)" textAnchor="middle">?</text>

          {/* Right Red Wing */}
          <text x="172" y="88" fontSize="13" transform="rotate(12, 172, 88)" textAnchor="middle">?</text>
          <text x="188" y="88" fontSize="11" transform="rotate(-8, 188, 88)" textAnchor="middle">?</text>
        </g>

        {/* heartbeat-and-checkmark path shadow / border */}
        <path 
          d="M 40,101 L 76,101 L 85,78 L 94,101 L 101,96 L 112,122 L 131,71 L 141,109 L 149,101 L 160,101" 
          stroke="#1a1a1a" 
          strokeWidth="7" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          fill="none" 
        />
        {/* heartbeat-and-checkmark path main white line */}
        <path 
          d="M 40,101 L 76,101 L 85,78 L 94,101 L 101,96 L 112,122 L 131,71 L 141,109 L 149,101 L 160,101" 
          stroke="#ffffff" 
          strokeWidth="4.2" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          fill="none" 
        />
      </g>

      {/* MCQ Top Arched Text matching original bubbly letter styling */}
      <text 
        className="logo-text-top"
        fill="#E13E33" 
        stroke="#1a1a1a"
        strokeWidth="1.6"
        paintOrder="stroke fill"
        letterSpacing="2"
      >
        <textPath href="#logo-top-text-path" startOffset="50%" textAnchor="middle" dy="4">
          MCQ
        </textPath>
      </text>

      {/* PULSE Bottom Arched Text matching original bubbly letter styling */}
      <text 
        className="logo-text-bottom"
        fill="#237142" 
        stroke="#1a1a1a"
        strokeWidth="1.6"
        paintOrder="stroke fill"
        letterSpacing="2"
      >
        <textPath href="#logo-bottom-text-path" startOffset="50%" textAnchor="middle" dy="-3">
          PULSE
        </textPath>
      </text>
    </svg>
  );
};
